"use client";
import type { Resume as ResumeType } from "../../lib/redux/types";
import type { ResumeSettings } from "../../lib/redux/settingsSlice";

interface ClassicBlueTemplateProps {
  resume: ResumeType;
  settings: ResumeSettings;
  isPDF: boolean;
}

export const ClassicBlueTemplate = ({ resume, settings, isPDF }: ClassicBlueTemplateProps) => {
  const getDocumentSize = () => {
    switch (settings.theme.documentSize) {
      case "A4":
        return { width: "210mm", height: "297mm" };
      case "Legal":
        return { width: "216mm", height: "356mm" };
      default: // Letter
        return { width: "216mm", height: "279mm" };
    }
  };

  const documentSize = getDocumentSize();

  // Function to split content into pages
  const splitContentIntoPages = () => {
    const sections = [];
    
    // Header section
    sections.push({
      type: 'header',
      content: (
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-blue-800 mb-3 uppercase tracking-wide">
            {resume.profile.name || "Your Name"}
          </h1>
          <div className="text-sm text-gray-600 space-y-1">
            {resume.profile.email && <div>{resume.profile.email}</div>}
            {resume.profile.phone && <div>{resume.profile.phone}</div>}
            {resume.profile.location && <div>{resume.profile.location}</div>}
            {resume.profile.url && <div>{resume.profile.url}</div>}
          </div>
        </div>
      )
    });

    // Summary section
    if (resume.profile.summary) {
      sections.push({
        type: 'summary',
        content: (
          <div className="mb-6">
            <div className="flex items-center mb-3">
              <h2 className="text-lg font-bold text-blue-800 uppercase tracking-wide mr-4">
                Summary
              </h2>
              <div className="flex-1 border-b border-gray-400"></div>
            </div>
            <p className="text-sm text-gray-800 leading-relaxed">
              {resume.profile.summary}
            </p>
          </div>
        )
      });
    }

    // Work Experience section
    if (resume.workExperiences.length > 0 && resume.workExperiences[0].company) {
      sections.push({
        type: 'workExperience',
        content: (
          <div className="mb-6">
            <div className="flex items-center mb-3">
              <h2 className="text-lg font-bold text-blue-800 uppercase tracking-wide mr-4">
                Work Experience
              </h2>
              <div className="flex-1 border-b border-gray-400"></div>
            </div>
            <div className="space-y-4">
              {resume.workExperiences.map((experience, idx) => (
                <div key={idx} className="mb-4">
                  <div className="flex justify-between items-start mb-1">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 mb-1">
                        {experience.jobTitle}
                      </h3>
                      {experience.company && (
                        <div className="text-base text-gray-700 mb-1">
                          {experience.company}
                        </div>
                      )}
                    </div>
                    <span className="text-sm text-gray-600">{experience.date}</span>
                  </div>
                  {experience.descriptions.length > 0 && (
                    <ul className="text-sm text-gray-800 space-y-1 mt-2">
                      {experience.descriptions.map((desc, descIdx) => (
                        <li key={descIdx} className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>{desc}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        )
      });
    }

    // Education section
    if (resume.educations.length > 0 && resume.educations[0].school) {
      sections.push({
        type: 'education',
        content: (
          <div className="mb-6">
            <div className="flex items-center mb-3">
              <h2 className="text-lg font-bold text-blue-800 uppercase tracking-wide mr-4">
                Education
              </h2>
              <div className="flex-1 border-b border-gray-400"></div>
            </div>
            <div className="space-y-4">
              {resume.educations.map((education, idx) => (
                <div key={idx} className="mb-4">
                  <div className="flex justify-between items-start mb-1">
                    <h3 className="font-bold text-gray-900">
                      {education.degree}
                    </h3>
                    <span className="text-sm text-gray-600">{education.date}</span>
                  </div>
                  <div className="text-sm text-gray-800 mb-2">{education.school}</div>
                  {education.gpa && (
                    <div className="text-sm text-gray-600 mb-2">Final CGPA: {education.gpa}</div>
                  )}
                  {education.descriptions.length > 0 && (
                    <ul className="text-sm text-gray-800 space-y-1">
                      {education.descriptions.map((desc, descIdx) => (
                        <li key={descIdx} className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>{desc}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        )
      });
    }

    // Projects section
    if (resume.projects.length > 0 && resume.projects[0].project) {
      sections.push({
        type: 'projects',
        content: (
          <div className="mb-6">
            <div className="flex items-center mb-3">
              <h2 className="text-lg font-bold text-blue-800 uppercase tracking-wide mr-4">
                Projects
              </h2>
              <div className="flex-1 border-b border-gray-400"></div>
            </div>
            <div className="space-y-4">
              {resume.projects.map((project, idx) => (
                <div key={idx} className="mb-4">
                  <div className="flex justify-between items-start mb-1">
                    <h3 className="font-bold text-gray-900">
                      {project.project}
                    </h3>
                    <span className="text-sm text-gray-600">{project.date}</span>
                  </div>
                  {project.descriptions.length > 0 && (
                    <ul className="text-sm text-gray-800 space-y-1 mt-2">
                      {project.descriptions.map((desc, descIdx) => (
                        <li key={descIdx} className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>{desc}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        )
      });
    }

    // Skills section
    if (resume.skills.featuredSkills.some(skill => skill.skill) || resume.skills.descriptions.length > 0) {
      sections.push({
        type: 'skills',
        content: (
          <div className="mb-6">
            <div className="flex items-center mb-3">
              <h2 className="text-lg font-bold text-blue-800 uppercase tracking-wide mr-4">
                Key Skills
              </h2>
              <div className="flex-1 border-b border-gray-400"></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {resume.skills.featuredSkills.some(skill => skill.skill) && (
                <div>
                  <ul className="text-sm text-gray-800 space-y-1">
                    {resume.skills.featuredSkills
                      .filter(skill => skill.skill)
                      .map((skill, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>{skill.skill}</span>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
              {resume.skills.descriptions.length > 0 && (
                <div>
                  <ul className="text-sm text-gray-800 space-y-1">
                    {resume.skills.descriptions.map((desc, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>{desc}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )
      });
    }

    // Custom section
    if (resume.custom.descriptions.length > 0) {
      sections.push({
        type: 'custom',
        content: (
          <div className="mb-6">
            <div className="flex items-center mb-3">
              <h2 className="text-lg font-bold text-blue-800 uppercase tracking-wide mr-4">
                Additional Information
              </h2>
              <div className="flex-1 border-b border-gray-400"></div>
            </div>
            <ul className="text-sm text-gray-800 space-y-1">
              {resume.custom.descriptions.map((desc, idx) => (
                <li key={idx} className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>{desc}</span>
                </li>
              ))}
            </ul>
          </div>
        )
      });
    }

    return sections;
  };

  const sections = splitContentIntoPages();

  // For PDF generation, return single page with CSS page breaks
  if (isPDF) {
    return (
      <div
        className="bg-white shadow-lg mx-auto"
        style={{
          width: documentSize.width,
          height: documentSize.height,
          fontFamily: settings.theme.fontFamily,
          fontSize: settings.theme.fontSize,
          padding: "20mm",
          boxSizing: "border-box",
          pageBreakAfter: "auto",
          pageBreakInside: "auto",
        }}
      >
        {sections.map((section, idx) => (
          <div key={idx} style={{ pageBreakInside: "avoid", pageBreakBefore: "auto" }}>
            {section.content}
          </div>
        ))}
      </div>
    );
  }

  // For browser preview, render as single continuous page
  return (
    <div className="bg-white shadow-lg mx-auto border border-gray-300" style={{ width: documentSize.width }}>
      <div
        style={{
          fontFamily: settings.theme.fontFamily,
          fontSize: settings.theme.fontSize,
          padding: "20mm",
          boxSizing: "border-box",
        }}
      >
        {sections.map((section, idx) => (
          <div key={idx}>
            {section.content}
          </div>
        ))}
      </div>
    </div>
  );
};
